import energyIndicator from "../../../assets/indicator-eui.svg";
import { useMemo } from "react";
import { CardTip } from "./Card";
import { P } from "../../../components/Common/Typo/typo";
import dayjs from "dayjs";

export const ENERGY_USE_INTENSITY = {
  TITLE: "Energy Use Intensity",
  TITLE_BADGE: "BEC standard",
  GAUGE_WIDTH: 267,
  GAUGE_HEIGHT: 20,
  GAUGE_INDICATOR: [0, 57, 82, 141, 171, 219],
  INDICATOR_SIZE: "28",
  UNIT: (
    <p>
      kWh/m<sup>2</sup>-y
    </p>
  ),
};

const useBarLocation = (value: number) => {
  return useMemo(() => {
    if (!value) return 0;
    let position;
    const indicatorLength = ENERGY_USE_INTENSITY.GAUGE_INDICATOR.length;
    const fraction = 100 / (indicatorLength - 1);
    for (let index = 1; index < indicatorLength; index++) {
      const current = ENERGY_USE_INTENSITY.GAUGE_INDICATOR[index];
      const prev = ENERGY_USE_INTENSITY.GAUGE_INDICATOR[index - 1];
      if (value <= current) {
        position = Math.round(
          ((value - prev) / (current - prev)) * fraction +
            fraction * (index - 1)
        );
        break;
      }
    }

    return Math.min(100, Math.max(position as number, 0));
  }, [value]);
};

interface EnergyUseIntensityProps {
  value: number;
  date: dayjs.Dayjs;
}

const EnergyUseIntensity: React.FC<EnergyUseIntensityProps> = ({
  value,
  date,
}: EnergyUseIntensityProps) => {
  const barLocation = useBarLocation(value);
  const labels = ["REF", "BEC", "HEPS", "ECON", "ZEB"];
  const EFFICIENCY_COLOR = [
    "#EF4337",
    "#F5A623",
    "#14B8B4",
    "#065BA9",
    "#AB63E8",
  ];
  const num = [219, 171, 141, 82, 57, 0];

  const listData = [
    "REF (219): อาคารอ้างอิง ใช้เป็นมาตรฐานเปรียบเทียบ",
    "BEC (171): ประสิทธิภาพต่ำสุด อาคารใช้พลังงานมาก",
    "HEPS (141): ดีขึ้นเล็กน้อย แต่ยังใช้พลังงานมาก",
    "ECON (82): ประสิทธิภาพสูงขึ้น ใช้พลังงานน้อยลง",
    "ZEB (57): ประสิทธิภาพสูงมาก ใช้พลังงานเกือบเป็นศูนย์",
  ];

  const getEfficiencyText = (value: number) => {
    if (value > 171) {
      return `${value} kWh/m²/ปี: การใช้พลังงานจริงของอาคารอยู่ในระดับอาคารอ้างอิง (REF) แสดงถึงประสิทธิภาพการใช้พลังงานที่ต้องปรับปรุงอย่างมาก เพื่อลดการใช้พลังงานให้ต่ำลง`;
    } else if (value > 141) {
      return `${value} kWh/m²/ปี: การใช้พลังงานจริงของอาคารอยู่ในระดับมาตรฐานอาคารประหยัดพลังงาน (BEC) แสดงถึงการใช้พลังงานที่เป็นไปตามเกณฑ์ขั้นต่ำ แต่ยังมีโอกาสในการพัฒนาประสิทธิภาพให้ดียิ่งขึ้น`;
    } else if (value > 82) {
      return `${value} kWh/m²/ปี: การใช้พลังงานจริงของอาคารอยู่ในระดับประสิทธิภาพพลังงานสูง (HEPS) แสดงถึงประสิทธิภาพการใช้พลังงานที่ดีกว่ามาตรฐาน BEC แต่ยังมีศักยภาพในการพัฒนาให้ประหยัดพลังงานมากขึ้น`;
    } else if (value > 57) {
      return `${value} kWh/m²/ปี: การใช้พลังงานจริงของอาคารอยู่ในระดับเศรษฐนิเวศน์ (ECON) แสดงถึงประสิทธิภาพการใช้พลังงานที่ดีมาก มีการใช้พลังงานอย่างคุ้มค่าและเป็นมิตรกับสิ่งแวดล้อม`;
    } else {
      return `${value} kWh/m²/ปี: การใช้พลังงานจริงของอาคารอยู่ในระดับอาคารพลังงานเกือบเป็นศูนย์ (ZEB) แสดงถึงประสิทธิภาพการใช้พลังงานที่สูงมาก เกือบถึงระดับที่อาคารสามารถผลิตพลังงานใช้เองได้เท่ากับที่ใช้`;
    }
  };

  return (
    <div className="bg-white border border-[#EDEFF9] shadow-wrapper rounded-[8px] mb-[8px] p-[10px] gap-[8px] h-full">
      <div className="flex gap-[6px] mb-[12px]">
        <div className="grow flex flex-col item-start justify-between relative">
          <div className="flex flex-col">
            <p className="text-[24px] font-bold text-[#1D1D1C]">
              ค่าความเข้มการใช้พลังงาน (EUI)
            </p>
            <p className="text-[16px] text-altoDark-medium -mt-2 mb-2">
              since Jan 1, {date.year()}
            </p>
          </div>
          <div className="relative self-start w-full">
            <div className="relative w-full flex gap-[2px] mb-[8px] justify-between">
              {labels.map((label, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: EFFICIENCY_COLOR[index],
                    borderRadius: `${index === 0 ? "10px" : "0"} ${
                      index === labels.length - 1 ? "10px" : "0"
                    } ${index === labels.length - 1 ? "10px" : "0"} ${
                      index === 0 ? "10px" : "0"
                    } `,
                  }}
                  className="flex justify-center items-center flex-1"
                >
                  <p className="text-[12px] font-bold text-white">{label}</p>
                </div>
              ))}
            </div>
            <div className="absolute w-full flex justify-between -mt-1">
              <p className="text-[12px] text-[#788796]">{num[0]}</p>
              <p className="text-[12px] text-[#788796]">
                {num[num.length - 1]}
              </p>
            </div>
            <div
              className="relative flex justify-between -mt-1"
              style={{
                left: `${50 / labels.length}%`,
                width: `${(1 - 1 / labels.length) * 100}%`,
              }}
            >
              {num.map((item, index) => {
                if (index === 0 || index === num.length - 1) return null;
                return (
                  <p className="text-[12px] text-center flex-1 text-[#788796]">
                    {item}
                  </p>
                );
              })}
            </div>
            {barLocation != undefined && (
              <img
                src={energyIndicator}
                alt={"energy indicator"}
                height={ENERGY_USE_INTENSITY.INDICATOR_SIZE}
                width={ENERGY_USE_INTENSITY.INDICATOR_SIZE}
                className={`absolute`}
                style={{
                  right: `calc(${barLocation}% -
                  ${Number(ENERGY_USE_INTENSITY.INDICATOR_SIZE) / 2}px
                )`,
                  top: "-25px",
                }}
              />
            )}
          </div>
        </div>
        <div className="relative flex justify-center items-center flex-col gap-[4px] rounded-[12px] p-[6px] w-[80px] h-[80px] border-[5px] border-[#00A4C3]">
          <p className="text-[32px] font-bold text-[#052745]">
            {value?.toLocaleString(undefined, {
              maximumFractionDigits: 2,
            })}
          </p>
          <p className="text-[16px] -mt-4 text-altoDark-medium text-center">
            {ENERGY_USE_INTENSITY.UNIT}
          </p>
        </div>
      </div>

      <CardTip
        title="ตัวเลขด้านบน (171, 141, 82, 57) แสดงระดับประสิทธิภาพพลังงานต่างๆ:"
        listData={listData}
      >
        <P fontSize="14px" color="#1E9282">
          {getEfficiencyText(value)}
        </P>
      </CardTip>
    </div>
  );
};

export default EnergyUseIntensity;
